import React, {useContext, useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import BarChartIcon from '@material-ui/icons/BarChart';
import ExtensionIcon from '@material-ui/icons/Extension';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import List from '@material-ui/core/List';
import Collapse from '@material-ui/core/Collapse';
import {Divider} from '@material-ui/core';
import {withRouter} from 'react-router';
import {useLocation} from 'react-router-dom';
import {AuthContext} from '../../../context/auth';
import {USER_ACCESS_LEVEL} from '../../../utils/constants';
import {getIndividualDashboardService} from '../../../services/dynamicDashboard.service';
import {LEARNING_PROGRESS_SECTION_CODE, learningProgramPresence} from '../../../pages/IndividualDashboard/learningProgramPresence';

const ORANGE = '#FF6900';
const GREY = '#A5A5A5';

// Matches the deck's own section dividers: "Baseline Results" groups
// Overview / Readiness / Context & Support (same pattern as
// NetworkDashboardList's Baseline Results group); "Learning Progress" is
// its own new top-level section per slide 8.
const INDIVIDUAL_DASHBOARD_NAV = [
    {
        code: 'baseline_results',
        label: 'Baseline Results',
        icon: BarChartIcon,
        children: [
            {label: 'Overview',    path: '/dq_pro/individual-dashboard/score-overview'},
            {label: 'Readiness',         path: '/dq_pro/individual-dashboard/competencies'},
            {label: 'Context & Support', path: '/dq_pro/individual-dashboard/context-support'},
        ],
    },
    {
        code: 'learning_progress',
        label: 'Learning Progress',
        icon: ExtensionIcon,
        path: '/dq_pro/individual-dashboard/learning-progress',
    },
];

const useStyles = makeStyles(() => ({
    item: {
        width: '92%',
        margin: '2px 10px',
        borderRadius: '10px',
        padding: '10px 12px',
        color: GREY,
        '&:hover': {backgroundColor: 'rgba(237,102,40,0.08)'},
    },
    itemCollapsed: {
        width: '65%',
    },
    itemActive: {
        backgroundColor: ORANGE,
        color: '#ffffff',
        '&:hover': {backgroundColor: ORANGE},
    },
    backItem: {
        backgroundColor: '#000000 !important',
        '& .MuiTypography-root': {color: '#ffffff'},
        '&:hover': {backgroundColor: '#222222 !important'},
    },
    subItem: {
        width: '85%',
        margin: '2px 10px 2px 32px',
        borderRadius: '10px',
        padding: '8px 12px',
        color: GREY,
        '&:hover': {backgroundColor: 'rgba(237,102,40,0.08)'},
    },
    subItemActive: {
        backgroundColor: ORANGE,
        color: '#ffffff',
        '&:hover': {backgroundColor: ORANGE},
    },
    icon: {minWidth: 'unset', marginRight: '12px'},
    label: {
        '& .MuiTypography-root': {fontSize: '14px', fontWeight: 600},
    },
    divider: {margin: '4px 10px'},
}));

const IndividualDashboardList = ({history, isDrawerOpen}) => {
    const classes = useStyles();
    const location = useLocation();
    const {authUser} = useContext(AuthContext);
    const isIndividualLearner = authUser?.access_level === USER_ACCESS_LEVEL.INDIVIDUAL_ACCESS_DQ_INSIDER_PORTAL;

    const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

    const [openGroups, setOpenGroups] = useState(() => {
        const initial = {};
        INDIVIDUAL_DASHBOARD_NAV.forEach((group) => {
            if (group.children) {
                initial[group.code] = group.children.some((c) => isActive(c.path));
            }
        });
        return initial;
    });
    const toggleGroup = (code) => setOpenGroups((prev) => ({...prev, [code]: !prev[code]}));

    // Learning Progress shows only once a learning program is linked (S_5 status). Fetched here
    // rather than through DashboardContext: the sidebar also renders on non-dashboard paths for
    // level-25 learners, and a concurrent context fetch would drop the page's own sections.
    // Hidden while loading, on failure and when unknown. Only learners (level 25) have an
    // Individual dashboard — other levels get a 400, so they are not asked.
    const [hasPrograms, setHasPrograms] = useState(false);
    const userId = authUser?._id;
    useEffect(() => {
        if (!isIndividualLearner) return undefined;
        let cancelled = false;
        getIndividualDashboardService({card_sections: LEARNING_PROGRESS_SECTION_CODE})
            .then((res) => { if (!cancelled) setHasPrograms(learningProgramPresence(res?.data) === 'linked'); })
            .catch(() => { if (!cancelled) setHasPrograms(false); });
        return () => { cancelled = true; };
    }, [userId, isIndividualLearner]);

    const NavItem = ({label, icon: Icon, path}) => {
        const active = isActive(path);
        return (
            <>
                <ListItem
                    button
                    onClick={() => history.push(path)}
                    className={`${classes.item} ${!isDrawerOpen ? classes.itemCollapsed : ''} ${active ? classes.itemActive : ''}`}
                >
                    <ListItemIcon className={classes.icon}>
                        <Icon style={{color: active ? '#ffffff' : ORANGE, fontSize: '22px'}}/>
                    </ListItemIcon>
                    {isDrawerOpen && <ListItemText className={classes.label} primary={label}/>}
                </ListItem>
                <Divider className={classes.divider}/>
            </>
        );
    };

    const SubItem = ({label, path}) => {
        const active = location.pathname === path;
        return (
            <ListItem
                button
                onClick={() => history.push(path)}
                className={`${classes.subItem} ${active ? classes.subItemActive : ''}`}
            >
                {isDrawerOpen && <ListItemText className={classes.label} primary={label}/>}
            </ListItem>
        );
    };

    const NavGroup = ({group}) => {
        const active = group.children.some((c) => isActive(c.path));
        const open = !!openGroups[group.code];
        const Icon = group.icon;
        return (
            <>
                <ListItem
                    button
                    onClick={() => {
                        if (isDrawerOpen) toggleGroup(group.code);
                        history.push(group.children[0].path);
                    }}
                    className={`${classes.item} ${!isDrawerOpen ? classes.itemCollapsed : ''} ${active ? classes.itemActive : ''}`}
                >
                    <ListItemIcon className={classes.icon}>
                        <Icon style={{color: active ? '#ffffff' : ORANGE, fontSize: '22px'}}/>
                    </ListItemIcon>
                    {isDrawerOpen && (
                        <>
                            <ListItemText className={classes.label} primary={group.label}/>
                            {open
                                ? <ExpandLess style={{color: active ? '#ffffff' : ORANGE}}/>
                                : <ExpandMore style={{color: active ? '#ffffff' : ORANGE}}/>
                            }
                        </>
                    )}
                </ListItem>
                <Divider className={classes.divider}/>

                <Collapse in={open && isDrawerOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        {group.children.map((c) => (
                            <SubItem key={c.path} label={c.label} path={c.path}/>
                        ))}
                    </List>
                </Collapse>
            </>
        );
    };

    return (
        <div style={{display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden'}}>
            <List style={{flexGrow: 1, paddingTop: '8px', overflowY: 'auto'}}>
                {!isIndividualLearner && (
                    <>
                        <ListItem
                            button
                            onClick={() => history.push('/my_workspace')}
                            className={`${classes.item} ${classes.backItem} ${!isDrawerOpen ? classes.itemCollapsed : ''}`}
                        >
                            <ListItemIcon className={classes.icon}>
                                <ArrowBackIcon style={{color: '#ffffff', fontSize: '22px'}}/>
                            </ListItemIcon>
                            {isDrawerOpen && <ListItemText className={classes.label} primary="Back to Workspace"/>}
                        </ListItem>
                        <Divider className={classes.divider}/>
                    </>
                )}

                {INDIVIDUAL_DASHBOARD_NAV.filter((g) => g.code !== 'learning_progress' || hasPrograms).map((group) => group.children
                    ? <NavGroup key={group.code} group={group}/>
                    : <NavItem key={group.code} label={group.label} icon={group.icon} path={group.path}/>
                )}
            </List>
        </div>
    );
};

export default withRouter(IndividualDashboardList);
